import { reactive } from 'vue';
import es from './locales/es.json';
import gl from './locales/gl.json';
import en from './locales/en.json';

const messages = { es, gl, en };
const SUPPORTED = ['es', 'gl', 'en'];
const DEFAULT_LANG = 'es';

// Reactive state — Vue tracks reads of state.lang inside t(), so templates that
// call $t(...) auto-rerender when the language changes.
const state = reactive({
    lang: detectInitialLanguage(),
});

function detectInitialLanguage() {
    // 1) Explicit user choice persisted in localStorage
    try {
        const stored = localStorage.getItem('lang');
        if (stored && SUPPORTED.includes(stored)) return stored;
    } catch (e) { /* localStorage unavailable (private mode) */ }

    // 2) Browser language ('es-ES', 'gl', 'en-US' → 'es' / 'gl' / 'en')
    const browser = (navigator.language || DEFAULT_LANG).toLowerCase().split('-')[0];
    if (SUPPORTED.includes(browser)) return browser;

    // 3) Fallback
    return DEFAULT_LANG;
}

function getNested(obj, path) {
    return path.split('.').reduce((o, k) => (o != null ? o[k] : undefined), obj);
}

/**
 * Translate a key. Supports {var} interpolation:
 *   t('frame_info', { current: 1, max: 5 })  →  "Frame: 1 / 5"
 *
 * Fallback chain: current language → English → key itself
 * (returning the key makes missing translations easy to spot).
 */
export function t(key, params) {
    let str = getNested(messages[state.lang], key)
           ?? getNested(messages.en, key)
           ?? key;

    if (params && typeof str === 'string') {
        for (const [k, v] of Object.entries(params)) {
            str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
        }
    }
    return str;
}

export function setLanguage(lang) {
    if (!SUPPORTED.includes(lang) || lang === state.lang) return;
    state.lang = lang;
    try { localStorage.setItem('lang', lang); } catch (e) { /* ignore */ }
    // Notify imperative DOM (innerHTML, tooltips) that needs to re-render.
    window.dispatchEvent(new CustomEvent('language-changed', { detail: lang }));
}

export function getLanguage() {
    return state.lang;
}

export const SUPPORTED_LANGUAGES = SUPPORTED;

// Vue plugin: registers $t on app.config.globalProperties so templates can use
// {{ $t('key') }} directly. Reactivity comes from `state` being a Vue reactive.
export const i18nPlugin = {
    install(app) {
        app.config.globalProperties.$t = t;
    },
};
